import React from "react";
import {Grid} from "@mui/material";
import {KeyData, KeyHistoryRecord, KeyVault} from "../../api/Model";
import {Location, locationTypeOptions, toLocationTypeEnum} from "../../components/location/KeyLocationShield";
import {SetState} from "../../util/FormHandlers";
import {filterSerializer} from "../../util/FilterSerializer";
import {TextSearchFilter} from "../../components/filter-fields/TextSearchFilter";
import {ClearFilterButton} from "../../components/filter-fields/ClearFilterButton";
import {StringEnumFilter} from "../../components/filter-fields/StringEnumFilter";
import {KeyVaultFilterOpt} from "../../components/filter-fields/KeyVaultFilter";
import {KeyProblem, KeyProblems} from "../../api/KeyProblemStore";
import {andFilter, applyFilter, filterText, matchMultiSelect, optionalFilter} from "../../util/FilterUtil";
import {LocationFilter} from "../../components/filter-fields/LocationFilter";

type SearchIn = 'full-history' | 'current-state';

export interface KeyFilter {
    location: Location[];
    search: string;
    keyVault: KeyVault | null;
    problem: KeyProblem;
    searchIn: SearchIn;
}


const includeHistoryOptions: { value: SearchIn, label: string }[] = [
    {value: 'full-history', label: "Full key history"},
    {value: 'current-state', label: "Current key state"},
];

const emptyKeyFilter: KeyFilter = {
    location: [],
    search: '',
    keyVault: null,
    problem: 'all',
    searchIn: 'current-state',
};

export const keyFilterSerializer = (keyVaults: KeyVault[]) => filterSerializer<KeyFilter>(
    emptyKeyFilter,
    b => {
        b.strArray('location');
        b.str('search');
        b.keyVault('keyVault', keyVaults);
        b.str('problem');
        b.str('searchIn');
    }
);

function keyMatch(row: KeyData, includeHistory: SearchIn, predicate: (record: KeyHistoryRecord) => boolean): boolean {
    return row.history.some(record => (includeHistory === 'full-history' || record.to == null) && predicate(record));
}

export function stringifyKey(keyData: KeyData, records: KeyHistoryRecord[]) {
    return [
        keyData.keyNumber,
        keyData.transponderId || '',
        ...keyData.tags.map(tag => tag.name),
        ...records.flatMap(record => {
            const {locationType, employee, guestFullName, guestCompany} = toLocationTypeEnum(record.location);
            return [
                record.notes,
                record.vault.name,
                locationTypeOptions[locationType].label,
                employee?.fullName,
                guestFullName,
                guestCompany,
            ];
        })
    ].filter(Boolean).join(' ');
}

export function filterKeyTableRows(tableRows: KeyData[], keyProblems: KeyProblems, filter: KeyFilter): KeyData[] {
    return applyFilter(tableRows, andFilter(
        optionalFilter(matchMultiSelect(filter.location), undefined, matchLocation =>
            key => keyMatch(key, filter.searchIn, record => matchLocation(record.location.type)),
        ),
        optionalFilter(filter.keyVault?.id, undefined, keyVaultId =>
            key => keyMatch(key, filter.searchIn, record => record.vault.id === keyVaultId),
        ),
        filterText(filter.search, key => stringifyKey(
            key,
            filter.searchIn === 'full-history' ? key.history : key.history.filter(record => !record.to),
        )),
        optionalFilter(filter.problem, 'all', (problem: KeyProblem) =>
            key => keyProblems.hasProblem(key, problem),
        ),
    ));
}

export function KeyTableFilter({filter, setFilter}: {
    filter: KeyFilter,
    setFilter: SetState<KeyFilter>,
}) {
    return (
        <Grid container spacing={2} alignItems="center">
            <KeyVaultFilterOpt filter={filter} setFilter={setFilter}/>
            <LocationFilter filter={filter} setFilter={setFilter}/>
            <StringEnumFilter label="Search in" filter={filter} setFilter={setFilter} property="searchIn"
                              options={includeHistoryOptions}/>
            <StringEnumFilter label="Problems" filter={filter} setFilter={setFilter} property="problem"
                              options={KeyProblems.options}/>
            <TextSearchFilter filter={filter} setFilter={setFilter} property="search"/>
            <ClearFilterButton emptyFilter={emptyKeyFilter} setFilter={setFilter}/>
        </Grid>
    );
}
